// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// Third party imports
import { DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { MultiColumnSelectorComponent } from '../../../../../management-common/component/common/multi-column-selector/multi-column-selector.component';

// Constant imports
import { ActionConstants } from '../../../../../_constants/action-constants';
import { Constants } from '../../../../../_constants/constants';
import { MessageInterval } from '../../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../../_constants/string-format-constants';

// Service imports
import { CommunityService } from '../../../../../_services/data-services/community.service';
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../../../_services/shared-services/server-error.service';
import { SearchService } from '../../../../../search/_services/data-services/search.service';
import { HelperService } from '../../../../../_services/shared-services/helper.service';

// Model imports
import { CommunitySelectorResultDto } from '../../../../../_models/community-selector-result-dto';
import { OwnerSearchResultDto } from '../../../../../search/_models/owner-search-result-dto';
import { SearchParametersDto } from '../../../../../search/_models/search-parameters-dto';
import { ARPaymentTransferAccountDetailsRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-account-details-raw-dto';
import { ARPaymentTransferAccountRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-account-raw-dto';
import { ARPaymentTransferRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-raw-dto';

@Component({
    selector: 'app-add-transfer-account',
    templateUrl: './add-transfer-account.component.html',
    styleUrls: ['./add-transfer-account.component.scss']
})
export class AddTransferAccountComponent implements OnInit {
    @ViewChild('transferAccountForm', { static: false }) transferAccountForm: DxFormComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() transferAccountParam: ARPaymentTransferRawDto;
    @Input() availableAmount: number;
    @Input() documentNo: string;
    @Input() action: string;
    @Input() editTransferAccount: ARPaymentTransferAccountDetailsRawDto;

    @Output() onAddTransferAccountClose = new EventEmitter<any>();

    readonly CURRENT_OWNER = 'Current Owners';
    readonly ALL_OWNERS = 'All Owners';

    defaultLabelLocation: string = Constants.defaultFormLabelLocation;
    addIcon: string = IconConstants.ADD_NEW;
    closeIcon: string = IconConstants.CLOSE;

    loadingVisible = false;
    communities: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];

    searchAccountButton: any;
    clearButton: any;
    searchText = '';
    currencyFormat: any;

    minAmount = 1;
    transferAccounts: ARPaymentTransferAccountDetailsRawDto;

    ownersType: any[] = [this.ALL_OWNERS, this.CURRENT_OWNER];
    selectedOwnersType = this.CURRENT_OWNER;
    propertyCurrentOwners: OwnerSearchResultDto[] = [];

    accountSelectorVisible = false;
    actionButtonText: string;

    constructor(
        private readonly communityService: CommunityService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly searchService: SearchService,
        private readonly helperService: HelperService
    ) {
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
        this.onSearchClick = this.onSearchClick.bind(this);
        this.onClearAccountsClick = this.onClearAccountsClick.bind(this);
    }

    ngOnInit() {
        if (Utils.equalsIgnoreCase(this.action, ActionConstants.ADD)) {
            this.transferAccounts = new ARPaymentTransferAccountDetailsRawDto();
            this.transferAccounts.creditAccountInfo = new ARPaymentTransferAccountRawDto();
            this.transferAccounts.creditAccountInfo.description = `Payment transfers from ${this.transferAccountParam.customerCode}. Ref Payment Doc Number '${this.documentNo}'`;
            this.transferAccounts.creditAccountInfo.communityId = this.transferAccountParam.communityId;
            this.transferAccounts.transferAmount = 0;
            this.transferAccounts.debitAccountDescription = '';
            this.actionButtonText = 'Add Account';
        } else {
            this.transferAccounts = this.editTransferAccount;
            this.transferAccountParam.communityId = this.editTransferAccount.creditAccountInfo.communityId;
            this.searchText = this.transferAccounts.creditAccountInfo.customerCode;
            this.actionButtonText = 'Update Account';
            this.onSearchClick();
        }
        this.getCommunities();
        this.searchAccountButton = {
            icon: 'more',
            type: 'default',
            onClick: this.onSearchClick
        };

        this.clearButton = {
            icon: 'clear',
            type: 'normal',
            stylingMode: 'text',
            onClick: this.onClearAccountsClick
        };
    }

    onHidden(e): void {
        this.visible = false;
        this.onAddTransferAccountClose.emit(false);
    }

    onAddAccountClick(): void {
        const resultStatus = this.checkMandatoryFields();
        if (Utils.isTrue(resultStatus.isValid)) {
            this.transferAccounts.transferAmount = this.precisionService.getRoundedNumber(
                this.transferAccounts.transferAmount
            );
            this.onAddTransferAccountClose.emit(this.transferAccounts);
            this.onHidden(null);
        } else {
            notify(resultStatus.message, 'error', MessageInterval.errorMsgInterval);
        }
    }

    onCommunitySelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedRows: any[] = selectedObject.selectedRow;
            if (Utils.notEquals(selectedRows[0].managementCompanyId, this.transferAccountParam.managementCompanyId)) {
                this.helperService
                    .getOkDialog(
                        'Error',
                        `The transfer payment tool can only be used for community in the same Management Company.`
                    )
                    .show()
                    .then(() => {
                        this.communityGridBoxValue = Utils.notNullAndDefined(this.transferAccountParam.communityId)
                            ? [this.transferAccountParam.communityId]
                            : [];
                    });
            } else if (
                Utils.isNullOrUndefined(this.transferAccountParam.communityId) ||
                Utils.notEquals(this.transferAccountParam.communityId, selectedRows[0].communityId)
            ) {
                this.transferAccountParam.communityId = selectedRows[0].communityId;
                this.searchText = '';
                this.propertyCurrentOwners = [];
            }
        } else if (Utils.notNullAndDefined(this.transferAccountParam.communityId)) {
            this.transferAccountParam.communityId = null;
            this.communityGridBoxValue = [];
            this.communitySelector.resetSelector();
        }
    }

    onSearchClick(): void {
        if (Utils.notNullOrEmpty(this.searchText) && this.searchText.length >= 3) {
            if (Utils.equalsIgnoreCase(this.selectedOwnersType, this.CURRENT_OWNER)) {
                this.searchCurrentOwners();
            } else {
                this.searchAllOwners();
            }
        } else {
            this.messageBox.showErrorMessage('Error', 'Please provide at least 3 characters to search');
        }
    }

    onClearAccountsClick(): void {
        this.searchText = null;
        this.transferAccounts.creditAccountInfo.customerCode = null;
        this.transferAccounts.creditAccountInfo.communityId = null;
        this.transferAccounts.communityName = null;
        this.transferAccounts.accountName = null;
        this.transferAccounts.debitAccountDescription = null;
        this.transferAccounts.creditAccountInfo.description = null;
    }

    onAccountSelect(e: OwnerSearchResultDto): void {
        this.accountSelectorVisible = false;
        if (Utils.isNullOrUndefined(this.searchText) || this.searchText.length < 3) {
            this.propertyCurrentOwners = [];
        } else {
            if (Utils.notNullAndDefined(e)) {
                this.searchText = e.owner;
                this.transferAccounts.creditAccountInfo.customerCode = e.accountNumber;
                this.transferAccounts.creditAccountInfo.communityId = e.communityId;
                this.transferAccounts.communityName = e.community;
                this.transferAccounts.accountName = e.combinedOwner;
                this.transferAccounts.debitAccountDescription = `Payment transfers (re:${this.documentNo}) to ${e.accountNumber}`;
                this.transferAccounts.creditAccountInfo.description = `Payment transfers (re:${this.documentNo}) from ${this.transferAccountParam.customerCode}`;
            }
        }
    }

    toggleAccountSelector(): void {
        this.accountSelectorVisible = !this.accountSelectorVisible;
    }

    private checkMandatoryFields(): any {
        const resultStatus = { isValid: true, message: null };

        if (Utils.isNullOrEmpty(this.transferAccounts.creditAccountInfo.customerCode)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Transfer account cannot be empty.';
        } else if (this.transferAccounts.transferAmount <= 0) {
            resultStatus.isValid = false;
            resultStatus.message = 'Transfer amount must be greater than Zero.';
        } else if (Utils.isNullOrEmpty(this.transferAccounts.debitAccountDescription)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Debit Description is required';
        }

        return resultStatus;
    }

    private searchCurrentOwners(): void {
        if (Utils.isNullOrEmpty(this.searchText)) {
            this.propertyCurrentOwners = [];
        } else {
            if (Utils.isNullOrEmpty(this.propertyCurrentOwners) || Utils.notNullAndDefined(this.searchText)) {
                const param: SearchParametersDto = new SearchParametersDto();
                param.clearCache = true;
                param.searchText = this.searchText;
                param.communityId = this.transferAccountParam.communityId;
                this.propertyCurrentOwners = [];
                this.accountSelectorVisible = false;
                this.loadingVisible = true;

                this.searchService.searchCurrentOwners(param).subscribe(
                    (data: OwnerSearchResultDto[]) => {
                        this.propertyCurrentOwners = data;
                        this.loadingVisible = false;

                        if (this.propertyCurrentOwners.length === 0) {
                            notify(
                                'There are no current owners matching the search term',
                                'error',
                                MessageInterval.defaultErrorMsgInterval
                            );
                        } else {
                            this.toggleAccountSelector();
                        }
                    },
                    (error) => {
                        this.errorService.displayError(error);
                        this.loadingVisible = false;
                    }
                );
            }
        }
    }

    private searchAllOwners(): void {
        const param: SearchParametersDto = new SearchParametersDto();
        param.clearCache = true;
        param.searchText = this.searchText;
        param.communityId = this.transferAccountParam.communityId;

        this.propertyCurrentOwners = [];
        this.accountSelectorVisible = false;
        this.loadingVisible = true;
        this.searchService.searchAllOwners(param).subscribe(
            (data: OwnerSearchResultDto[]) => {
                this.propertyCurrentOwners = data;
                this.loadingVisible = false;

                if (Utils.isNullOrEmpty(this.propertyCurrentOwners)) {
                    notify(
                        'There are no owners matching the search term',
                        'error',
                        MessageInterval.defaultErrorMsgInterval
                    );
                } else {
                    this.toggleAccountSelector();
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunities(): void {
        this.loadingVisible = true;
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.communityService.getCommunities().subscribe(
            (results) => {
                this.communities = results;
                if (Utils.notNullAndDefined(this.transferAccountParam.communityId)) {
                    this.communityGridBoxValue = [this.transferAccountParam.communityId];
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
